import { useCallback, useEffect, useState } from 'react';
import { useJobStatus } from '../api/getJobStatus';
import { queryClient } from 'lib/react-query';
import { useEnqueueSnackbar } from 'hooks/useEnqueueSnackbar';
import type { JobStatusResponse, ResultEntry } from 'types/shared';

const isDev = process.env.NODE_ENV === 'development';
const DEV_SIMULATE_DURATION_MS = 15_000;
const DEV_MOCK_RESULT: ResultEntry[] = [
  { name: 'Dev Ad 1', account_id: 'dev-account-1', ad_set: 'Dev AdSet', creative: 'Dev Creative', success: true, is_successful: true, ad_id: 'dev-ad-123', output: 'Mock success', message: 'Mock success', advertiser_id: 'dev-adv-1', adgroup_name: 'Dev AdGroup', creative_name: 'Dev Creative' },
  { name: 'Dev Ad 2', account_id: 'dev-account-2', ad_set: 'Dev AdSet 2', creative: 'Dev Creative 2', success: false, is_successful: false, ad_id: '', output: 'Mock failure', message: 'Mock failure example', advertiser_id: 'dev-adv-2', adgroup_name: 'Dev AdGroup 2', creative_name: 'Dev Creative 2' },
];

const useJobStatusCheck = <TResult = ResultEntry>() => {
  const { onShowErrorAlert, onShowAlert } = useEnqueueSnackbar();
  const [jobId, setJobId] = useState<string | null>(null);
  const [refetchInterval, setRefetchInterval] = useState<number | null>(null);
  const [devData, setDevData] = useState<JobStatusResponse<TResult> | null>(null);

  const {
    data: apiData,
    isLoading,
    isError,
    error,
    isFetching,
    isPending,
    isRefetching,
  } = useJobStatus<TResult>({
    params: { job_id: jobId },
    config: {
      enabled: !!jobId && !isDev,
      refetchInterval: refetchInterval ?? undefined,
    },
  });

  const data = isDev ? devData : apiData;

  // Dev mode: simulate loading → finished after DEV_SIMULATE_DURATION_MS
  useEffect(() => {
    if (!isDev || !jobId) return;

    setDevData({ status: 'started' });

    const timer = setTimeout(() => {
      setDevData({
        status: 'finished',
        // Dev-only simulation; the rows are TikTok/Facebook-shaped regardless of TResult.
        result: DEV_MOCK_RESULT as unknown as TResult[],
      });
      console.log('[DEV] Job finished with mock data');
    }, DEV_SIMULATE_DURATION_MS);

    return () => { clearTimeout(timer); };
  }, [jobId]);

  const clearJobStatus = () => {
    setRefetchInterval(null);
    setJobId(null);
    setDevData(null);
    queryClient.removeQueries({
      queryKey: ['api', 'v1', 'job_status'],
    });
  };

  useEffect(() => {
    if (error) {
      onShowErrorAlert(
        (error as { cause: { error: string } })?.cause.error ??
          'Failed to check job status'
      );
    }
    if (isError) {
      setJobId(null);
    }
  }, [onShowErrorAlert, error, isError]);

  useEffect(() => {
    if (jobId && !isDev) {
      setRefetchInterval(15000);
    }
  }, [jobId]);

  useEffect(() => {
    if (isDev || !data) return;

    switch (data.status) {
      case 'queued':
      case 'scheduled':
      case 'started':
        // Continue polling
        break;
      case 'finished':
        console.log('Job finished:', data.result);
        onShowAlert('Job finished');
        setRefetchInterval(null); // Clear interval, but not job id to retain cached data
        break;
      case 'failed':
        console.error('Job failed:', data.error);
        onShowErrorAlert('Job failed');
        setJobId(null); // Stop polling
        setRefetchInterval(null); // Clear interval, but not job id to retain cached data
        break;
      default:
        console.log('Unknown job status:', data.status);
    }
  }, [data, onShowAlert, onShowErrorAlert]);

  const runJubStatusCheck = useCallback(
    (jobId: string) => {
      setJobId(jobId);
    },
    [setJobId]
  );

  return {
    isLoading:
      isDev ? (!!jobId && data?.status !== 'finished') :
      isLoading || isRefetching || (data && data.status !== 'finished'),
    isRefetching,
    isPending,
    isError,
    isFetching,
    isFinished: !!data && data.status === 'finished',
    data,
    runJubStatusCheck,
    clearJobStatus,
  };
};

export default useJobStatusCheck;
